import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useDistributor, useAdmin, useSuperAdmin } from '../lib/auth';
import { authed } from '../lib/api';
import { LogOut, MapPin, Plus, Check, Edit3, KeyRound, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ChangeMyPasswordCard, ChangeMyEmailCard } from '../components/CredentialCards';

/**
 * Account page — role-aware.
 *
 * - Distributor: editable person_name / business_name / email + saved addresses.
 * - Employee Admin & Super Admin: editable display_name + mobile. Email is
 *   displayed here (read-only) and can be changed only via Security page.
 */
export default function Account() {
    const { user, refresh, signOut: signOutDistributor } = useDistributor();
    const { admin, signOut: signOutAdmin } = useAdmin();
    const { superAdmin, signOut: signOutSuper } = useSuperAdmin();
    const nav = useNavigate();

    // Most-privileged wins.
    const role = superAdmin ? 'super_admin' : (admin ? 'admin' : (user ? 'distributor' : null));
    if (!role) return null;

    const doSignOut = async () => {
        try {
            if (role === 'super_admin') { await authed('super_admin', { method: 'POST', url: '/superadmin/auth/logout' }).catch(() => {}); signOutSuper(); }
            else if (role === 'admin') { await authed('admin', { method: 'POST', url: '/admin/auth/logout' }).catch(() => {}); signOutAdmin(); }
            else { signOutDistributor(); }
        } finally { nav('/login', { replace: true }); }
    };

    return (
        <div className="min-h-screen bg-sdf-ivory">
            <Header />
            <main className="max-w-3xl mx-auto px-4 py-6 space-y-4 pb-24">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-sdf-text">Account</h1>
                {role === 'distributor' ? (
                    <DistributorProfile user={user} refresh={refresh} />
                ) : (
                    <AdminProfile realm={role === 'super_admin' ? 'super_admin' : 'admin'} />
                )}
                {role === 'distributor' && <AddressesSection user={user} refresh={refresh} />}
                {role === 'super_admin' && <SuperAdminCredentialsSection />}
                <section className="pt-4">
                    <button onClick={doSignOut} className="w-full card-soft p-4 flex items-center gap-3 justify-center text-red-700 hover:bg-red-50 font-bold" data-testid="sign-out-btn">
                        <LogOut size={18} /> <span>Sign Out</span>
                    </button>
                </section>
            </main>
            <Footer />
        </div>
    );
}

/**
 * Super Admin credentials management on the Account page (Batch B2 Bug-Fix Pass).
 *
 * · Fetches TOTP status so the credential cards know whether to require the
 *   6-digit Google Authenticator code.
 * · Two collapsible sections keep the account page tidy — the user opens the
 *   one they need.
 * · Employee Admins do NOT see any credential-management UI here per user spec:
 *   all Employee Admin credentials are managed only from the Super Admin
 *   Dashboard → Settings → Security.
 */
function SuperAdminCredentialsSection() {
    const [me, setMe] = useState(null);
    const [showPw, setShowPw] = useState(false);
    const [showEm, setShowEm] = useState(false);
    useEffect(() => {
        (async () => {
            try {
                const r = await authed('super_admin', { method: 'GET', url: '/superadmin/me' });
                setMe(r.data);
            } catch { /* ignore */ }
        })();
    }, []);
    const totpEnabled = !!me?.totp_enabled;
    return (
        <section className="space-y-3" data-testid="super-admin-creds-section">
            <div className="text-sdf-red font-bold text-xs tracking-widest mt-2">SECURITY</div>
            <button
                onClick={() => setShowPw(!showPw)}
                className="w-full card-soft p-4 flex items-center gap-3 text-left hover:bg-sdf-cream/30 transition-colors"
                data-testid="acc-toggle-pw"
            >
                <KeyRound size={18} className="text-sdf-red" />
                <div className="flex-1">
                    <div className="font-bold text-sdf-text">Change Password</div>
                    <div className="text-[11px] text-sdf-text-2">{totpEnabled ? 'Requires current password + Google Authenticator code.' : 'Requires current password.'}</div>
                </div>
                <div className="text-xs text-sdf-text-2">{showPw ? 'Close' : 'Open'}</div>
            </button>
            {showPw && <ChangeMyPasswordCard realm="super_admin" totpEnabled={totpEnabled} />}

            <button
                onClick={() => setShowEm(!showEm)}
                className="w-full card-soft p-4 flex items-center gap-3 text-left hover:bg-sdf-cream/30 transition-colors"
                data-testid="acc-toggle-em"
            >
                <Mail size={18} className="text-sdf-red" />
                <div className="flex-1">
                    <div className="font-bold text-sdf-text">Change Email</div>
                    <div className="text-[11px] text-sdf-text-2">{me?.email ? `Currently: ${me.email}` : 'Update the email used to sign in.'}</div>
                </div>
                <div className="text-xs text-sdf-text-2">{showEm ? 'Close' : 'Open'}</div>
            </button>
            {showEm && <ChangeMyEmailCard realm="super_admin" totpEnabled={totpEnabled} currentEmail={me?.email || ''} onUpdated={(e) => setMe(m => m ? { ...m, email: e } : m)} />}
        </section>
    );
}

function DistributorProfile({ user, refresh }) {
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({});
    const [msg, setMsg] = useState('');
    useEffect(() => {
        if (user) setForm({ person_name: user.person_name || '', business_name: user.business_name || '', email: user.email || '' });
    }, [user]);
    const save = async () => {
        try {
            await authed('distributor', { method: 'PUT', url: '/dist/profile', data: form });
            await refresh(); setEditing(false); setMsg('Profile updated.');
            setTimeout(() => setMsg(''), 2000);
        } catch (e) { setMsg(e.response?.data?.detail || 'Update failed'); }
    };
    if (!user) return null;
    return (
        <section className="card-soft p-5">
            <div className="flex items-center justify-between">
                <div className="text-sdf-red font-bold text-xs tracking-widest">PROFILE INFORMATION</div>
                {!editing && <button onClick={() => setEditing(true)} className="btn-outline" data-testid="edit-profile"><Edit3 size={12} /> Edit</button>}
            </div>
            {!editing ? (
                <div className="mt-4 space-y-3">
                    <Info k="Person Name" v={user.person_name} />
                    <Info k="Mobile Number" v={`+91 ${user.mobile}`} />
                    <Info k="Email Address" v={user.email || ''} placeholder="Not provided" />
                </div>
            ) : (
                <div className="mt-4 space-y-3">
                    <FInput label="Person Name" value={form.person_name} onChange={(v) => setForm({ ...form, person_name: v })} tid="acc-person" />
                    <FInput label="Business Name" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} tid="acc-business" />
                    <FInput label="Email Address" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} tid="acc-email" />
                    <div className="flex items-center gap-2">
                        <button onClick={save} className="btn-red"><Check size={14} /> Save</button>
                        <button onClick={() => setEditing(false)} className="btn-outline">Cancel</button>
                    </div>
                </div>
            )}
            {msg && <div className="mt-2 text-xs text-green-700">{msg}</div>}
        </section>
    );
}

function AdminProfile({ realm }) {
    const [me, setMe] = useState(null);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({ display_name: '', mobile: '' });
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');
    const load = async () => {
        const r = await authed(realm, { method: 'GET', url: '/admin/auth/me' });
        setMe(r.data);
        setForm({ display_name: r.data.display_name || '', mobile: r.data.mobile || '' });
    };
    useEffect(() => { load(); }, [realm]);  // eslint-disable-line
    const save = async () => {
        setErr(''); setMsg('');
        try {
            await authed(realm, { method: 'PUT', url: '/admin/auth/profile', data: form });
            await load();
            setEditing(false); setMsg('Profile updated.');
            setTimeout(() => setMsg(''), 2000);
        } catch (e) { setErr(e.response?.data?.detail || 'Update failed'); }
    };
    if (!me) return <section className="card-soft p-5"><div className="text-sdf-text-2 text-sm">Loading profile…</div></section>;
    const roleLabel = realm === 'super_admin' ? 'Super Admin' : 'Employee Admin';
    return (
        <section className="card-soft p-5" data-testid="admin-profile-card">
            <div className="flex items-center justify-between">
                <div className="text-sdf-red font-bold text-xs tracking-widest">{roleLabel.toUpperCase()} PROFILE</div>
                {!editing && <button onClick={() => setEditing(true)} className="btn-outline" data-testid="edit-admin-profile"><Edit3 size={12} /> Edit</button>}
            </div>
            {!editing ? (
                <div className="mt-4 space-y-3">
                    <Info k="Name" v={me.display_name} placeholder="Not set" />
                    <Info k="Mobile Number" v={me.mobile ? `+91 ${me.mobile}` : ''} placeholder="Not set" />
                    <Info k="Email Address" v={me.email} />
                    <p className="text-[11px] text-sdf-text-2 mt-2">
                        {realm === 'super_admin'
                            ? 'Use the SECURITY section below to change your email or password.'
                            : 'Employee Admin credentials (email and password) can only be changed by the Super Admin from Settings → Security.'}
                    </p>
                </div>
            ) : (
                <div className="mt-4 space-y-3">
                    <FInput label="Name" value={form.display_name} onChange={(v) => setForm({ ...form, display_name: v })} tid="admin-acc-name" />
                    <FInput label="Mobile Number" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v.replace(/\D/g, '').slice(0, 10) })} tid="admin-acc-mobile" />
                    <Info k="Email Address" v={me.email} />
                    {err && <div className="text-xs text-red-700" data-testid="admin-profile-error">{err}</div>}
                    <div className="flex items-center gap-2">
                        <button onClick={save} className="btn-red" data-testid="admin-profile-save"><Check size={14} /> Save</button>
                        <button onClick={() => { setEditing(false); setForm({ display_name: me.display_name || '', mobile: me.mobile || '' }); }} className="btn-outline">Cancel</button>
                    </div>
                </div>
            )}
            {msg && <div className="mt-2 text-xs text-green-700" data-testid="admin-profile-success">{msg}</div>}
        </section>
    );
}

function AddressesSection({ user, refresh }) {
    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(emptyAddr());
    const [msg, setMsg] = useState('');
    const list = user?.addresses || [];

    const startEdit = (a) => {
        setEditId(a.id);
        setForm({ label: a.label || 'Warehouse', full_name: a.full_name, phone: a.phone, line1: a.line1, line2: a.line2 || '', city: a.city, state: a.state, pin_code: a.pin_code, is_default: !!a.is_default });
        setShowForm(true);
    };
    const save = async () => {
        setMsg('');
        for (const k of ['full_name', 'phone', 'line1', 'city', 'state', 'pin_code']) {
            if (!String(form[k] || '').trim()) { setMsg('Please fill all required fields'); return; }
        }
        try {
            if (editId) {
                await authed('distributor', { method: 'DELETE', url: `/dist/addresses/${editId}` });
                await authed('distributor', { method: 'POST', url: '/dist/addresses', data: form });
            } else {
                await authed('distributor', { method: 'POST', url: '/dist/addresses', data: form });
            }
            await refresh();
            setShowForm(false); setEditId(null); setForm(emptyAddr());
        } catch (e) { setMsg(e.response?.data?.detail || 'Failed to save address'); }
    };
    const del = async (id) => { await authed('distributor', { method: 'DELETE', url: `/dist/addresses/${id}` }); await refresh(); };
    const makeDefault = async (id) => { await authed('distributor', { method: 'PUT', url: `/dist/addresses/${id}/default` }); await refresh(); };
    return (
        <section className="card-soft p-5">
            <div className="flex items-center justify-between">
                <div className="text-sdf-red font-bold text-xs tracking-widest flex items-center gap-2"><MapPin size={12} /> SAVED DELIVERY ADDRESSES</div>
                {!showForm && (<button onClick={() => { setShowForm(true); setEditId(null); setForm(emptyAddr()); }} className="btn-red" data-testid="add-address-btn"><Plus size={14} /> Add</button>)}
            </div>
            {list.length === 0 && !showForm && <div className="mt-4 text-sm text-sdf-text-2">No addresses saved yet. Add one to speed up checkout.</div>}
            <div className="mt-4 space-y-3">
                {list.map((a) => (
                    <div key={a.id} className="rounded-2xl border border-sdf-line p-4 flex items-start gap-3 bg-white">
                        <div className="flex-1">
                            <div className="flex items-center gap-2">
                                <span className="font-bold">{a.label || 'Address'}</span>
                                {a.is_default && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-800">DEFAULT</span>}
                            </div>
                            <div className="text-sm text-sdf-text mt-1">{a.full_name} · +91 {a.phone}</div>
                            <div className="text-sm text-sdf-text-2">{a.line1}{a.line2 ? `, ${a.line2}` : ''}</div>
                            <div className="text-sm text-sdf-text-2">{a.city}, {a.state} - {a.pin_code}</div>
                            <div className="mt-2 flex items-center gap-3">
                                {!a.is_default && <button onClick={() => makeDefault(a.id)} className="text-xs text-sdf-red font-bold">Set Default</button>}
                                <button onClick={() => startEdit(a)} className="text-xs text-sdf-text-2 font-bold">Edit</button>
                                <button onClick={() => del(a.id)} className="text-xs text-red-700 font-bold">Delete</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {showForm && (
                <div className="mt-4 p-4 rounded-2xl bg-sdf-cream/60 border border-sdf-line space-y-3">
                    <div className="text-sdf-red font-bold text-xs tracking-widest">{editId ? 'EDIT ADDRESS' : 'NEW ADDRESS'}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <FInput label="Label" value={form.label} onChange={(v) => setForm({ ...form, label: v })} tid="addr-label" />
                        <FInput label="Full Name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} tid="addr-name" />
                        <FInput label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v.replace(/\D/g, '') })} tid="addr-phone" />
                        <FInput label="Line 1" value={form.line1} onChange={(v) => setForm({ ...form, line1: v })} tid="addr-l1" />
                        <FInput label="Line 2" value={form.line2} onChange={(v) => setForm({ ...form, line2: v })} tid="addr-l2" />
                        <FInput label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} tid="addr-city" />
                        <FInput label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} tid="addr-state" />
                        <FInput label="PIN Code" value={form.pin_code} onChange={(v) => setForm({ ...form, pin_code: v.replace(/\D/g, '') })} tid="addr-pin" />
                    </div>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} /> Set as default</label>
                    {msg && <div className="text-sm text-red-700">{msg}</div>}
                    <div className="flex items-center gap-2">
                        <button onClick={save} className="btn-red"><Check size={14} /> Save</button>
                        <button onClick={() => { setShowForm(false); setEditId(null); setForm(emptyAddr()); setMsg(''); }} className="btn-outline">Cancel</button>
                    </div>
                </div>
            )}
        </section>
    );
}

function emptyAddr() { return { label: 'Warehouse', full_name: '', phone: '', line1: '', line2: '', city: '', state: '', pin_code: '', is_default: false }; }

function Info({ k, v, placeholder = '—' }) {
    return (
        <div>
            <div className="text-[10px] font-bold text-sdf-text-2 tracking-widest">{k.toUpperCase()}</div>
            <div className={`text-sm font-semibold ${v ? 'text-sdf-text' : 'text-sdf-text-2'}`}>{v || placeholder}</div>
        </div>
    );
}
function FInput({ label, type = 'text', value, onChange, tid }) {
    return (
        <div>
            <label className="text-[10px] font-bold text-sdf-text-2 tracking-widest">{label.toUpperCase()}</label>
            <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full border border-sdf-line rounded-xl px-3 py-2.5 outline-none text-sm bg-white" data-testid={tid} />
        </div>
    );
}
